import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../context/auth';
import { dayjs } from '../../../../../date-util';
import StaticDocumentsAPI, {
	StaticDocumentResponse,
} from '../../api/staticDocuments';
import {
	MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH,
	STATIC_DOCS_ACTIONS_DELETE_TOOLTIP_TEXT,
	STATIC_DOCS_ACTIONS_DOWNLOAD_TOOLTIP_TEXT,
	STATIC_DOCS_CONFIRM_DELETE_MODAL_CANCEL_BUTTON,
	STATIC_DOCS_CONFIRM_DELETE_MODAL_DELETE_BUTTON,
	STATIC_DOCS_CONFIRM_DELETE_MODAL_HEADER,
	STATIC_DOCS_CONFIRM_DELETE_MODAL_MESSAGE,
	STATIC_DOCS_HEADER,
	STATIC_DOCS_HELPER_TEXT,
	STATIC_DOCS_LAST_UPDATED_LABEL,
	STATIC_DOCS_MODAL_HEADER,
	STATIC_DOCS_REFRESHING_LABEL,
	STATIC_DOCS_ROW_DATE_FORMAT,
	STATIC_DOCS_SUBHEADER_ACTIONS,
	STATIC_DOCS_SUBHEADER_EFFECTIVE_DATE,
	STATIC_DOCS_SUBHEADER_FILE_NAME,
} from '../../data/constants';
import { handleModalClose } from '../../utils/handleModalClose';
import AddStaticDocumentForm from './components/AddStaticDocumentsForm';
import FilterStaticDocuments from './components/FilterTable';
import TableHeader, { HeadCell, Order } from './components/TableHeader';

export const TABLE_KEY = 'staticDocuments';

const headCells: HeadCell[] = [
	{
		id: 'fileName',
		label: STATIC_DOCS_SUBHEADER_FILE_NAME(),
	},
	{
		id: 'effectiveTimestamp',
		label: STATIC_DOCS_SUBHEADER_EFFECTIVE_DATE(),
	},
	{
		id: 'locator',
		disableSort: true,
		align: 'right',
		label: STATIC_DOCS_SUBHEADER_ACTIONS(),
	},
];

export const useFilterParams = () => {
	const locationSearch = useLocation().search;
	const params = new URLSearchParams(locationSearch);

	return {
		fileName: params.get('fileName') ?? '',
		asOfDate: params.get('asOfDate') ?? '',
	};
};

export default function StaticDocuments() {
	const { tenantHostname } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const { asOfDate, fileName } = useFilterParams();
	const { data, isLoading, isRefetching, dataUpdatedAt, refetch } =
		StaticDocumentsAPI.useStaticDocuments(
			[TABLE_KEY, tenantId || tenantHostname, asOfDate, fileName],
			{
				asOfTimestamp: parseInt(asOfDate, 10),
				fileName: fileName,
			},
			authToken,
			tenantHostname,
		);

	const { mutateAsync: deleteStaticDocument, isLoading: isDeleting } =
		StaticDocumentsAPI.useDeleteStaticDocumentMutation(
			[TABLE_KEY, tenantId || tenantHostname, asOfDate, fileName],
			authToken,
			tenantHostname,
		);

	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] =
		React.useState<keyof StaticDocumentResponse>('effectiveTimestamp');
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(50);
	const [isDocumentModalOpen, setIsDocumentModalOpen] = React.useState(false);
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
		React.useState(false);
	const [documentPendingDelete, setDocumentPendingDelete] =
		React.useState<StaticDocumentResponse>();

	const handleDocumentModalOpen = () => {
		setIsDocumentModalOpen(true);
	};

	const handleDocumentModalClose = () => {
		setIsDocumentModalOpen(false);
	};

	const handleConfirmDeleteModalOpen = (
		selectedDocument: StaticDocumentResponse,
	) => {
		setIsConfirmDeleteModalOpen(true);
		setDocumentPendingDelete(selectedDocument);
	};

	const handleConfirmDeleteModalClose = () => {
		setIsConfirmDeleteModalOpen(false);
		setDocumentPendingDelete(null);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof StaticDocumentResponse,
	) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleDeleteDocument = async (document: StaticDocumentResponse) => {
		await deleteStaticDocument(document.locator);
		handleConfirmDeleteModalClose();
	};

	return (
		<>
			<Stack direction='column' height='100%' spacing={2}>
				<Stack direction='row' spacing={2}>
					<Typography variant='h3'>{STATIC_DOCS_HEADER()}</Typography>
					<Button
						variant='contained'
						color='secondary'
						startIcon={<AddIcon />}
						onClick={handleDocumentModalOpen}
					>
						{STATIC_DOCS_MODAL_HEADER()}
					</Button>
				</Stack>
				<FilterStaticDocuments
					asOfDate={parseInt(asOfDate, 10)}
					fileName={fileName}
					onApply={({ asOfDate, fileName }) => {
						const params = new URLSearchParams({});

						if (asOfDate) {
							params.set('asOfDate', asOfDate + '');
						}
						if (fileName) {
							params.set('fileName', fileName);
						}
						navigate(location.pathname + '?' + params.toString());
					}}
					refetch={refetch}
				/>

				<Stack direction='row' spacing={2} justifyContent='space-between'>
					<Stack
						direction='row'
						spacing={1}
						alignContent='center'
						color='#777777'
					>
						<InfoOutlinedIcon fontSize='small' />
						<Typography variant='body2' align='left'>
							{STATIC_DOCS_HELPER_TEXT()}
						</Typography>
					</Stack>
					<Typography variant='body2' align='right'>
						{isLoading ? STATIC_DOCS_REFRESHING_LABEL() : null}
						{!isLoading && isRefetching ? STATIC_DOCS_REFRESHING_LABEL() : null}
						{!isLoading && !isRefetching
							? STATIC_DOCS_LAST_UPDATED_LABEL() +
							  dayjs(dataUpdatedAt).local().format('lll')
							: null}
					</Typography>
				</Stack>

				<Paper
					sx={{
						flex: 1,
						overflowY: 'auto',
					}}
				>
					<TableContainer sx={{ maxHeight: '100%' }}>
						<Table size='small' stickyHeader>
							<TableHeader
								headCells={headCells}
								order={order}
								orderBy={orderBy}
								onRequestSort={handleRequestSort}
							/>
							<TableBody>
								{data
									?.sort((a, b) => {
										const direction = order === 'asc' ? 1 : -1;
										if (a[orderBy] < b[orderBy]) {
											return -1 * direction;
										}
										if (a[orderBy] > b[orderBy]) {
											return 1 * direction;
										}
										return 0;
									})
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((file, index) => (
										<TableRow
											key={file.locator}
											hover
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
										>
											<TableCell>{file.fileName}</TableCell>
											<TableCell sx={{ minWidth: 280 }}>
												<Typography fontWeight={500} variant='body2'>
													{dayjs(file.effectiveTimestamp).format(
														STATIC_DOCS_ROW_DATE_FORMAT(),
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Stack
													direction='row'
													spacing={1}
													justifyContent='flex-end'
												>
													<Tooltip
														title={STATIC_DOCS_ACTIONS_DOWNLOAD_TOOLTIP_TEXT()}
													>
														<IconButton
															aria-label='download file'
															href={file.url}
														>
															<DownloadIcon />
														</IconButton>
													</Tooltip>
													<Tooltip
														title={STATIC_DOCS_ACTIONS_DELETE_TOOLTIP_TEXT()}
													>
														<IconButton
															aria-label='delete'
															onClick={() => handleConfirmDeleteModalOpen(file)}
														>
															<DeleteIcon />
														</IconButton>
													</Tooltip>
												</Stack>
											</TableCell>
										</TableRow>
									))}
							</TableBody>
							{data && !isLoading && data.length > 0 && (
								<TableFooter>
									<TableRow>
										<TableCell
											colSpan={3}
											sx={{
												backgroundColor: 'white.lightest',
												position: 'sticky',
												bottom: 0,
												borderTop: '1px solid',
												borderColor: 'white.dark',
											}}
										>
											<TablePagination
												posit
												rowsPerPageOptions={[10, 25, 50]}
												component='div'
												count={data?.length}
												rowsPerPage={rowsPerPage}
												page={page}
												onPageChange={handleChangePage}
												onRowsPerPageChange={handleChangeRowsPerPage}
											/>
										</TableCell>
									</TableRow>
								</TableFooter>
							)}
						</Table>
					</TableContainer>
				</Paper>
			</Stack>
			<Dialog
				open={isConfirmDeleteModalOpen}
				onClose={(e, r) => handleModalClose(r, handleConfirmDeleteModalClose)}
			>
				<DialogTitle>{STATIC_DOCS_CONFIRM_DELETE_MODAL_HEADER()}</DialogTitle>
				<DialogContent sx={{ width: MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH }}>
					{STATIC_DOCS_CONFIRM_DELETE_MODAL_MESSAGE(
						documentPendingDelete?.fileName,
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleConfirmDeleteModalClose} color='neutralButton'>
						{STATIC_DOCS_CONFIRM_DELETE_MODAL_CANCEL_BUTTON()}
					</Button>
					<Button
						variant='contained'
						color='error'
						disabled={isDeleting}
						onClick={() => handleDeleteDocument(documentPendingDelete)}
					>
						{STATIC_DOCS_CONFIRM_DELETE_MODAL_DELETE_BUTTON()}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={isDocumentModalOpen}
				onClose={(e, r) => handleModalClose(r, handleDocumentModalClose)}
				sx={{ overflowX: 'hidden' }}
			>
				<DialogTitle>{STATIC_DOCS_MODAL_HEADER()}</DialogTitle>
				<DialogContent sx={{ width: MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH }}>
					<AddStaticDocumentForm
						tenantHostname={tenantHostname}
						handleClose={handleDocumentModalClose}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
